import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Mic, Play, Pause, Volume2, VolumeX, Download } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2];

const fmtTime = (s) => {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

const PlayerEmptyState = ({ loading, audioError }) => {
  if (loading) {
    return (
      <div className="flex items-center gap-3 py-4">
        <div className="animate-spin rounded-full h-5 w-5 border-2 border-teal-500 border-t-transparent" />
        <span className="text-sm text-slate-500">Loading recording...</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3 py-4 text-slate-400">
      <Mic className="w-5 h-5" />
      <span className="text-sm">
        {audioError ? 'Unable to load audio — recording may have expired.' : 'No recording available for this call.'}
      </span>
    </div>
  );
};

const ProgressBar = ({ currentTime, duration, onSeek }) => {
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  return (
    <div className="space-y-1">
      <div className="relative group">
        <input
          type="range"
          min={0}
          max={duration || 100}
          step={0.1}
          value={currentTime}
          onChange={onSeek}
          className="w-full h-2 rounded-full appearance-none cursor-pointer accent-teal-500"
          data-testid="seek-slider"
          style={{ background: `linear-gradient(to right, #14b8a6 ${progress}%, #e2e8f0 ${progress}%)` }}
        />
      </div>
      <div className="flex justify-between text-xs text-slate-400">
        <span data-testid="current-time">{fmtTime(currentTime)}</span>
        <span data-testid="duration">{fmtTime(duration)}</span>
      </div>
    </div>
  );
};

const PlayerControls = ({
  playing, onTogglePlay,
  muted, volume, onVolumeChange, onToggleMute,
  speed, onSpeedChange,
  onDownload,
}) => {
  const effectiveVol = muted ? 0 : volume;
  return (
    <div className="flex items-center gap-3 flex-wrap">
      <button
        onClick={onTogglePlay}
        className="flex items-center justify-center w-10 h-10 rounded-full bg-teal-500 hover:bg-teal-600 text-white transition-colors shadow-sm flex-shrink-0"
        data-testid="play-pause-btn"
      >
        {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
      </button>

      <button onClick={onToggleMute} className="text-slate-400 hover:text-slate-600 transition-colors" data-testid="mute-btn">
        {effectiveVol === 0 ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
      </button>
      <input
        type="range" min={0} max={1} step={0.05}
        value={effectiveVol}
        onChange={onVolumeChange}
        className="w-20 h-1.5 rounded-full appearance-none cursor-pointer accent-teal-500"
        style={{ background: `linear-gradient(to right, #14b8a6 ${effectiveVol * 100}%, #e2e8f0 ${effectiveVol * 100}%)` }}
        data-testid="volume-slider"
      />

      <div className="flex-1" />

      <div className="flex items-center gap-1">
        {SPEEDS.map(s => (
          <button
            key={s}
            onClick={() => onSpeedChange(s)}
            className={`text-xs px-2 py-0.5 rounded font-medium transition-colors ${speed === s ? 'bg-teal-100 text-teal-700' : 'text-slate-400 hover:text-slate-600'}`}
            data-testid={`speed-${s}x`}
          >
            {s}x
          </button>
        ))}
      </div>

      <button
        onClick={onDownload}
        className="text-slate-400 hover:text-slate-600 transition-colors ml-1"
        title="Download recording"
        data-testid="download-btn"
      >
        <Download className="w-4 h-4" />
      </button>
    </div>
  );
};

export const RecordingPlayer = ({ callId }) => {
  const [loading, setLoading] = useState(true);
  const [recordingUrl, setRecordingUrl] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [audioError, setAudioError] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/calls/${callId}/recording`);
        if (!cancelled) setRecordingUrl(res.data.recording_url || null);
      } catch (e) {
        console.debug('RecordingPlayer: no recording URL returned', e?.response?.status || e?.message);
        if (!cancelled) setRecordingUrl(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [callId]);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) audio.pause();
    else audio.play().catch(() => setAudioError(true));
    setPlaying(!playing);
  }, [playing]);

  const handleSeek = (e) => {
    const audio = audioRef.current;
    if (!audio) return;
    const val = parseFloat(e.target.value);
    audio.currentTime = val;
    setCurrentTime(val);
  };

  const handleVolume = (e) => {
    const val = parseFloat(e.target.value);
    setVolume(val);
    if (audioRef.current) {
      audioRef.current.volume = val;
      setMuted(val === 0);
    }
  };

  const toggleMute = () => {
    if (!audioRef.current) return;
    const next = !muted;
    setMuted(next);
    audioRef.current.muted = next;
  };

  const setPlaybackSpeed = (s) => {
    setSpeed(s);
    if (audioRef.current) audioRef.current.playbackRate = s;
  };

  const handleDownload = () => {
    if (!recordingUrl) return;
    const a = document.createElement('a');
    a.href = recordingUrl;
    a.download = `recording-${callId}.wav`;
    a.click();
  };

  return (
    <Card className="border-slate-200" data-testid="recording-player-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2">
          <Mic className="w-5 h-5 text-teal-500" />
          Call Recording
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading || !recordingUrl || audioError ? (
          <PlayerEmptyState loading={loading} audioError={audioError} />
        ) : (
          <div className="space-y-4" data-testid="audio-player">
            <audio
              ref={audioRef}
              src={recordingUrl}
              onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime || 0)}
              onLoadedMetadata={() => setDuration(audioRef.current?.duration || 0)}
              onEnded={() => setPlaying(false)}
              onError={() => setAudioError(true)}
              preload="metadata"
            />
            <ProgressBar currentTime={currentTime} duration={duration} onSeek={handleSeek} />
            <PlayerControls
              playing={playing} onTogglePlay={togglePlay}
              muted={muted} volume={volume}
              onVolumeChange={handleVolume} onToggleMute={toggleMute}
              speed={speed} onSpeedChange={setPlaybackSpeed}
              onDownload={handleDownload}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};
